import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Mail, Loader2, CheckCircle, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const PROPERTY_TYPES = ['Casa', 'Apartamento', 'Oficina', 'Terreno', 'Local', 'Bodega'];
const TRANSACTION_TYPES = ['Comprar', 'Rentar'];
const DEPARTMENTS = ['Guatemala', 'Sacatepéquez', 'Escuintla', 'Quetzaltenango'];

const NewsletterSignup = ({ variant = "default", className = "" }) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    property_types: [],
    transaction_types: [],
    departments: []
  });

  const togglePreference = (category, value) => {
    setPreferences(prev => {
      const current = prev[category];
      const updated = current.includes(value)
        ? current.filter(v => v !== value)
        : [...current, value];
      return { ...prev, [category]: updated };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !email.includes("@")) {
      toast.error(t('newsletter.invalidEmail'));
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/api/newsletter/subscribe`, { 
        email,
        preferences: {
          property_types: preferences.property_types,
          transaction_types: preferences.transaction_types,
          departments: preferences.departments
        }
      });
      setSubscribed(true);
      toast.success(t('newsletter.success'));
      setEmail("");
    } catch (error) {
      if (error.response?.status === 409) {
        toast.info(t('newsletter.alreadySubscribed'));
      } else {
        toast.error(t('newsletter.error'));
      }
    } finally {
      setLoading(false);
    }
  };

  if (subscribed) {
    return (
      <div className={`text-center ${className}`}>
        <CheckCircle className="w-12 h-12 text-[#D4AF37] mx-auto mb-3" />
        <p className="text-white font-medium">{t('newsletter.thankYou')}</p>
        <p className="text-[#A3A3A3] text-sm mt-1">{t('newsletter.confirmationSent')}</p>
      </div>
    );
  }

  // Compact variant for footer
  if (variant === "compact") {
    return (
      <form onSubmit={handleSubmit} className={`${className}`}>
        <p className="text-white font-medium mb-3">{t('newsletter.title')}</p>
        <div className="flex gap-2">
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('newsletter.placeholder')}
            className="h-10 bg-white/5 border-white/10 text-white placeholder:text-white/40 flex-1"
            data-testid="newsletter-email-compact"
          />
          <Button
            type="submit"
            disabled={loading}
            className="h-10 bg-[#D4AF37] text-black hover:bg-[#FFE182] px-4"
            data-testid="newsletter-submit-compact"
          >
            {loading ? <Loader2 className="animate-spin" size={16} /> : <Mail size={16} />}
          </Button>
        </div>
      </form>
    );
  }

  // Default full variant
  return (
    <div className={`bg-[#121212] border border-white/5 p-8 ${className}`} data-testid="newsletter-signup">
      <div className="max-w-lg mx-auto text-center">
        <Mail className="w-10 h-10 text-[#D4AF37] mx-auto mb-4" />
        <h3 className="text-xl text-white font-medium">{t('newsletter.title')}</h3>
        <p className="text-[#A3A3A3] mt-2 mb-6">{t('newsletter.subtitle')}</p>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('newsletter.placeholder')}
              className="h-12 bg-black/40 border-white/10 text-white placeholder:text-white/40 flex-1"
              data-testid="newsletter-email"
            />
            <Button
              type="submit"
              disabled={loading}
              className="h-12 bg-[#D4AF37] text-black hover:bg-[#FFE182] font-medium px-6"
              data-testid="newsletter-submit"
            >
              {loading ? (
                <Loader2 className="animate-spin" size={20} />
              ) : (
                t('common.subscribe')
              )}
            </Button>
          </div>
          
          {/* Preferences Toggle */}
          <button
            type="button"
            onClick={() => setShowPreferences(!showPreferences)}
            className="flex items-center gap-2 mx-auto text-[#A3A3A3] hover:text-[#D4AF37] text-sm transition-colors"
          >
            {showPreferences ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            {t('newsletter.customizeAlerts', 'Personalizar alertas')}
          </button>
          
          {/* Preferences Panel */}
          {showPreferences && (
            <div className="mt-4 p-4 bg-black/40 rounded text-left space-y-4">
              {/* Transaction Types */}
              <div>
                <p className="text-white text-sm font-medium mb-2">{t('newsletter.interests', '¿Qué te interesa?')}</p>
                <div className="flex flex-wrap gap-3">
                  {TRANSACTION_TYPES.map(type => (
                    <label key={type} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={preferences.transaction_types.includes(type)}
                        onCheckedChange={() => togglePreference('transaction_types', type)}
                        className="border-white/30 data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37]"
                      />
                      <span className="text-[#A3A3A3] text-sm">{type}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              {/* Property Types */}
              <div>
                <p className="text-white text-sm font-medium mb-2">{t('newsletter.propertyTypes', 'Tipos de propiedad')}</p>
                <div className="flex flex-wrap gap-3">
                  {PROPERTY_TYPES.map(type => (
                    <label key={type} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={preferences.property_types.includes(type)}
                        onCheckedChange={() => togglePreference('property_types', type)}
                        className="border-white/30 data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37]"
                      />
                      <span className="text-[#A3A3A3] text-sm">{type}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              {/* Departments */}
              <div>
                <p className="text-white text-sm font-medium mb-2">{t('newsletter.departments', 'Departamentos')}</p>
                <div className="flex flex-wrap gap-3">
                  {DEPARTMENTS.map(dept => (
                    <label key={dept} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={preferences.departments.includes(dept)}
                        onCheckedChange={() => togglePreference('departments', dept)}
                        className="border-white/30 data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37]"
                      />
                      <span className="text-[#A3A3A3] text-sm">{dept}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              <p className="text-[#525252] text-xs">
                {t('newsletter.preferencesNote', 'Deja vacío para recibir todas las propiedades')}
              </p>
            </div>
          )}
        </form>
        
        <p className="text-[#525252] text-xs mt-4">
          {t('newsletter.privacy')}
        </p>
      </div>
    </div>
  );
};

export default NewsletterSignup;
